import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  HandCoins, Home as HomeIcon, GraduationCap, Stethoscope, Baby, Accessibility,
  Sprout, TreePine, LifeBuoy, HandHelping, Users
} from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const iconMap = { a: HandCoins, b: HomeIcon, c: GraduationCap, d: Stethoscope, e: Baby, f: Accessibility, g: Sprout, h: TreePine, i: LifeBuoy };

const areaKeys = ["a", "b", "c", "d", "e", "f", "g", "h", "i"];

const empty = { name: "", email: "", phone: "", city: "", age: "", areas: [], availability: "", skills: "", message: "", show_publicly: true };

const Volunteer = () => {
  const { t, lang } = useApp();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [volunteers, setVolunteers] = useState([]);

  useEffect(() => {
    api.get("/volunteers/public").then((r) => setVolunteers(r.data)).catch(() => {});
  }, []);

  const toggleArea = (k) => {
    setForm((s) => ({
      ...s,
      areas: s.areas.includes(k) ? s.areas.filter((x) => x !== k) : [...s.areas, k],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.areas.length === 0) {
      toast.error(lang === "hi" ? "कम से कम एक क्षेत्र चुनें" : "Please select at least one area");
      return;
    }
    setLoading(true);
    try {
      await api.post("/volunteers", {
        ...form,
        age: form.age ? Number(form.age) : null,
      });
      toast.success(lang === "hi" ? "धन्यवाद! हम जल्द ही आपसे संपर्क करेंगे।" : "Thank you! We will reach out to you soon.");
      setForm(empty);
      // reload public list (approved ones only)
      const r = await api.get("/volunteers/public");
      setVolunteers(r.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="volunteer-page">
      <div className="mb-12 animate-fade-up max-w-3xl">
        <p className="text-xs uppercase tracking-[0.25em] text-[#C95B24] font-semibold mb-3 flex items-center gap-2">
          <HandHelping className="w-4 h-4" /> {lang === "hi" ? "स्वयंसेवक बनें" : "Become a Volunteer"}
        </p>
        <h1 className="font-serif-display text-5xl sm:text-6xl text-[#1B4332] mb-4 leading-[1.05]">
          {lang === "hi" ? "सेवा में जुड़ें" : "Join Our Seva Family"}
        </h1>
        <p className="text-lg text-[#4A4A4A]">
          {lang === "hi"
            ? "अपना समय दें, कौशल दें, या बस अपनी करुणा दें — किसी भी क्षेत्र में जो आपको छू ले।"
            : "Give your time, your skills, or simply your compassion — join us in any area that moves your heart."}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Form */}
        <form onSubmit={submit} className="lg:col-span-3 card-tactile p-8 md:p-10 space-y-5" data-testid="volunteer-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label htmlFor="v-name">{lang === "hi" ? "पूरा नाम*" : "Full Name*"}</Label>
              <Input id="v-name" data-testid="volunteer-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2" /></div>
            <div><Label htmlFor="v-email">{lang === "hi" ? "ईमेल*" : "Email*"}</Label>
              <Input id="v-email" type="email" data-testid="volunteer-email-input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div><Label htmlFor="v-phone">{lang === "hi" ? "फ़ोन*" : "Phone*"}</Label>
              <Input id="v-phone" data-testid="volunteer-phone-input" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2" /></div>
            <div><Label htmlFor="v-city">{lang === "hi" ? "शहर" : "City"}</Label>
              <Input id="v-city" data-testid="volunteer-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-2" /></div>
            <div><Label htmlFor="v-age">{lang === "hi" ? "आयु" : "Age"}</Label>
              <Input id="v-age" type="number" min="12" max="100" data-testid="volunteer-age-input" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="mt-2" /></div>
          </div>

          <div>
            <Label className="mb-3 block">{lang === "hi" ? "किन क्षेत्रों में सेवा देना चाहते हैं?*" : "Which areas do you want to help with?*"}</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="volunteer-areas">
              {areaKeys.map((k) => {
                const Ico = iconMap[k];
                const active = form.areas.includes(k);
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => toggleArea(k)}
                    data-testid={`volunteer-area-${k}`}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm text-left transition-all ${active ? "border-[#C95B24] bg-[#FCEAD9] text-[#8E3B1D]" : "border-[#1B4332]/15 bg-white text-[#1B4332] hover:border-[#C95B24]/40"}`}
                  >
                    <Ico className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1">{k.toUpperCase()}. {t(`${k}_title`)}</span>
                    {active && <span className="text-[#C95B24] font-bold">✓</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>{lang === "hi" ? "उपलब्धता" : "Availability"}</Label>
              <Select value={form.availability} onValueChange={(v) => setForm({ ...form, availability: v })}>
                <SelectTrigger data-testid="volunteer-availability" className="mt-2"><SelectValue placeholder={lang === "hi" ? "चुनें" : "Choose"} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Weekdays">{lang === "hi" ? "कार्य-दिवस" : "Weekdays"}</SelectItem>
                  <SelectItem value="Weekends">{lang === "hi" ? "सप्ताहांत" : "Weekends"}</SelectItem>
                  <SelectItem value="Evenings">{lang === "hi" ? "शाम" : "Evenings"}</SelectItem>
                  <SelectItem value="Flexible">{lang === "hi" ? "लचीला" : "Flexible"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="v-skills">{lang === "hi" ? "कौशल / विशेषज्ञता" : "Skills / Expertise"}</Label>
              <Input id="v-skills" data-testid="volunteer-skills-input" placeholder={lang === "hi" ? "जैसे: शिक्षक, डॉक्टर, फोटोग्राफर" : "e.g. teacher, doctor, photographer"} value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} className="mt-2" />
            </div>
          </div>

          <div>
            <Label htmlFor="v-msg">{lang === "hi" ? "संदेश (वैकल्पिक)" : "Message (optional)"}</Label>
            <Textarea id="v-msg" data-testid="volunteer-message-input" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2" />
          </div>

          <div className="flex items-center gap-3">
            <Checkbox id="v-pub" checked={form.show_publicly} onCheckedChange={(v) => setForm({ ...form, show_publicly: !!v })} data-testid="volunteer-show-publicly" />
            <Label htmlFor="v-pub" className="cursor-pointer text-sm">
              {lang === "hi" ? "मेरा नाम सार्वजनिक स्वयंसेवक सूची में दिखाएँ" : "Show my name on the public volunteer list"}
            </Label>
          </div>

          <Button type="submit" disabled={loading} data-testid="volunteer-submit-btn" className="btn-saffron rounded-full w-full py-6 text-base">
            <HandHelping className="w-5 h-5 mr-2" /> {loading ? "..." : (lang === "hi" ? "स्वयंसेवक बनें" : "Join as Volunteer")}
          </Button>
          <p className="text-xs text-[#4A4A4A] text-center">
            {lang === "hi" ? "व्यवस्थापक द्वारा अनुमोदन के बाद आपका नाम सार्वजनिक सूची में दिखेगा।" : "Your name will appear on the public list after admin approval."}
          </p>
        </form>

        {/* Public volunteer list */}
        <aside className="lg:col-span-2 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-[#C95B24]" />
            <h3 className="font-serif-display text-2xl text-[#1B4332]">
              {lang === "hi" ? "हमारे स्वयंसेवक" : "Our Volunteers"}
            </h3>
            <span className="ml-auto text-sm text-[#4A4A4A]">{volunteers.length}</span>
          </div>
          {volunteers.length === 0 ? (
            <div className="card-tactile p-8 text-center text-sm text-[#4A4A4A]" data-testid="volunteer-list-empty">
              {lang === "hi" ? "पहले स्वयंसेवक बनें!" : "Be the first to join!"}
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
              {volunteers.map((v, i) => (
                <div key={i} className="card-tactile p-4" data-testid={`volunteer-card-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="font-semibold text-[#1B4332]">{v.name}</div>
                      {v.city && <div className="text-xs text-[#4A4A4A]">{v.city}</div>}
                    </div>
                  </div>
                  {v.areas && v.areas.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {v.areas.map((a) => (
                        <span key={a} className="text-[10px] uppercase tracking-wider bg-[#F4EFE6] text-[#1B4332] px-2 py-0.5 rounded-full">
                          {a.toUpperCase()}
                        </span>
                      ))}
                    </div>
                  )}
                  {v.skills && <div className="text-xs text-[#4A4A4A] mt-2 italic">{v.skills}</div>}
                </div>
              ))}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default Volunteer;
